import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, addMonths, endOfMonth, isAfter, addDays } from "date-fns";
import { TrendingUp, TrendingDown } from "lucide-react";

const CashFlowSkeleton = () => (
  <Card>
    <CardHeader>
      <CardTitle><Skeleton className="h-6 w-32" /></CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-40 w-full" />
      </div>
    </CardContent>
  </Card>
);

// Fixed monthly expenses - could eventually be configurable
const FIXED_MONTHLY_EXPENSES = {
  rent: 3000,
  payroll: 15000,
  utilities: 500,
  insurance: 800,
  software: 1200,
  other: 2500
};

const getPaymentTermsDays = (terms: string) => {
  switch(terms) {
    case '30 Days': return 30;
    case '60 Days': return 60;
    case '90 Days': return 90;
    case 'Net 30': return 30;
    case 'Net 60': return 60;
    case 'Due on Receipt': return 0;
    default: return 60;
  }
};

export function CashFlowChart() {
  const { data: cashFlowData, isLoading, error } = useQuery({
    queryKey: ["cash-flow-chart"],
    queryFn: async () => {
      // Get current bank balances
      const { data: bankAccounts, error: bankError } = await supabase
        .from('bank_accounts')
        .select('id, name, opening_balance, opening_balance_date')
        .eq('is_active', true);

      if (bankError) {
        console.error('Error fetching bank accounts:', bankError);
        throw bankError;
      }

      // Get bank transactions to calculate current balances
      const { data: transactions, error: transactionsError } = await supabase
        .from('bank_transactions')
        .select('bank_account_id, amount, date')
        .order('date', { ascending: true });

      if (transactionsError) {
        console.error('Error fetching transactions:', transactionsError);
      }

      // Calculate current cash position
      let currentCashPosition = 0;
      bankAccounts?.forEach(account => {
        let accountBalance = Number(account.opening_balance || 0);
        
        // Add all transactions for this account
        transactions?.forEach(transaction => {
          if (transaction.bank_account_id === account.id) {
            accountBalance += Number(transaction.amount);
          }
        });
        
        currentCashPosition += accountBalance;
      });

      // Get outstanding invoices (sent but not paid)
      const { data: outstandingInvoices, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          amount,
          issue_date,
          due_date,
          clients!inner(payment_terms)
        `)
        .in('status', ['sent', 'overdue']);

      if (invoicesError) {
        console.error('Error fetching outstanding invoices:', invoicesError);
      }

      // Get future invoices from projects and proposals
      const currentDate = new Date();
      const endOfYear = new Date(currentDate.getFullYear(), 11, 31);

      // Get projects that will generate invoices
      const { data: futureProjects, error: projectsError } = await supabase
        .from('projects')
        .select(`
          sold_for,
          extra_value,
          payment_schedule,
          extra_value_payment_schedule,
          start_date,
          delivered_date,
          clients!inner(payment_terms)
        `)
        .in('status', ['won', 'in_progress'])
        .lte('start_date', endOfYear.toISOString().split('T')[0]);

      if (projectsError) {
        console.error('Error fetching future projects:', projectsError);
      }

      // Get expected invoices from accepted proposals
      const { data: acceptedProposals, error: proposalsError } = await supabase
        .from('proposals')
        .select(`
          final_value,
          value,
          expected_invoicing_date,
          clients!inner(payment_terms)
        `)
        .eq('status', 'accepted')
        .not('expected_invoicing_date', 'is', null)
        .lte('expected_invoicing_date', endOfYear.toISOString().split('T')[0]);

      if (proposalsError) {
        console.error('Error fetching accepted proposals:', proposalsError);
      }

      // Generate 12-month cash flow forecast
      const cashFlowData = [];
      let runningCashBalance = currentCashPosition;
      const totalFixedExpenses = Object.values(FIXED_MONTHLY_EXPENSES).reduce((sum, expense) => sum + expense, 0);
      
      for (let i = 0; i < 12; i++) {
        const forecastMonth = addMonths(startOfMonth(currentDate), i);
        const monthStart = startOfMonth(forecastMonth);
        const monthEnd = endOfMonth(forecastMonth);
        const monthName = format(forecastMonth, 'MMM yyyy');
        
        let monthlyInflows = 0;
        let monthlyOutflows = 0;

        // Calculate inflows from outstanding invoices (based on payment terms)
        outstandingInvoices?.forEach(invoice => {
          if (invoice.issue_date && invoice.clients?.payment_terms) {
            const paymentTermsDays = getPaymentTermsDays(invoice.clients.payment_terms);
            const expectedPaymentDate = addDays(new Date(invoice.issue_date), paymentTermsDays);
            
            if (expectedPaymentDate >= monthStart && expectedPaymentDate <= monthEnd) {
              monthlyInflows += Number(invoice.amount);
            }
          }
        });

        // Calculate inflows from future projects
        futureProjects?.forEach(project => {
          if (project.payment_schedule && project.clients?.payment_terms) {
            const paymentSchedule = project.payment_schedule as any;
            const projectValue = Number(project.sold_for || 0) + Number(project.extra_value || 0);
            const paymentTermsDays = getPaymentTermsDays(project.clients.payment_terms);

            if (paymentSchedule.type === 'once_off' && paymentSchedule.invoicing_date) {
              const expectedPaymentDate = addDays(new Date(paymentSchedule.invoicing_date), paymentTermsDays);
              if (expectedPaymentDate >= monthStart && expectedPaymentDate <= monthEnd) {
                monthlyInflows += projectValue;
              }
            } else if (paymentSchedule.type === 'split' && paymentSchedule.splits) {
              // Handle split payments
              paymentSchedule.splits.forEach((split: any) => {
                let invoiceDate: Date | null = null;
                
                if (split.trigger === 'at_start' && project.start_date) {
                  invoiceDate = new Date(project.start_date);
                } else if (split.trigger === 'at_delivery' && project.delivered_date) {
                  invoiceDate = new Date(project.delivered_date);
                }
                
                if (invoiceDate) {
                  const expectedPaymentDate = addDays(invoiceDate, paymentTermsDays);
                  if (expectedPaymentDate >= monthStart && expectedPaymentDate <= monthEnd) {
                    monthlyInflows += projectValue * (split.percentage / 100);
                  }
                }
              });
            }
          }
        });

        // Calculate inflows from accepted proposals
        acceptedProposals?.forEach(proposal => {
          if (proposal.expected_invoicing_date && proposal.clients?.payment_terms) {
            const paymentTermsDays = getPaymentTermsDays(proposal.clients.payment_terms);
            const expectedPaymentDate = addDays(new Date(proposal.expected_invoicing_date), paymentTermsDays);
            
            if (expectedPaymentDate >= monthStart && expectedPaymentDate <= monthEnd) {
              const proposalValue = Number(proposal.final_value || proposal.value || 0);
              monthlyInflows += proposalValue;
            }
          }
        });

        // Calculate fixed monthly outflows
        monthlyOutflows += totalFixedExpenses;

        // Calculate net cash flow for the month
        const netCashFlow = monthlyInflows - monthlyOutflows;
        runningCashBalance += netCashFlow;

        cashFlowData.push({
          month: monthName,
          inflows: Math.round(monthlyInflows),
          outflows: Math.round(monthlyOutflows),
          netFlow: Math.round(netCashFlow),
          cashBalance: Math.round(runningCashBalance),
          isCurrentMonth: i === 0
        });
      }

        return {
          data: cashFlowData,
          currentCash: Math.round(currentCashPosition),
          fixedExpenses: totalFixedExpenses
        };
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
  });

  if (isLoading) {
    return <CashFlowSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cash Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-40 text-muted-foreground">
            Failed to load cash flow data
          </div>
        </CardContent>
      </Card>
    );
  }

  const data = cashFlowData?.data || [];
  const currentCash = cashFlowData?.currentCash || 0;
  const fixedExpenses = cashFlowData?.fixedExpenses || 0;
  
  const currentMonthBalance = data[0]?.cashBalance || 0;
  const endOfYearBalance = data[data.length - 1]?.cashBalance || 0;
  const projectedChange = endOfYearBalance - currentMonthBalance;
  const changePercentage = currentMonthBalance !== 0 ? (projectedChange / currentMonthBalance * 100).toFixed(1) : "0";

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1
    }).format(amount);
  };

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Cash Flow</span>
          <div className="flex items-center gap-2">
            {projectedChange >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-600" />
            )}
            <span className={`text-sm font-medium ${
              projectedChange >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {projectedChange >= 0 ? '+' : ''}{changePercentage}% projected change
            </span>
          </div>
        </CardTitle>
        
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 pt-4">
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(currentCash)}
            </div>
            <div className="text-sm text-muted-foreground">Current Cash</div>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(fixedExpenses)}
            </div>
            <div className="text-sm text-muted-foreground">Monthly Fixed Costs</div>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className={`text-2xl font-bold ${
              endOfYearBalance >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {formatCurrency(endOfYearBalance)}
            </div>
            <div className="text-sm text-muted-foreground">Projected Year-End</div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="cashBalanceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="inflowsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.6}/>
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="outflowsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.6}/>
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              interval={1}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [
                formatCurrency(value), 
                name === 'cashBalance' ? 'Cash Balance' : 
                name === 'inflows' ? 'Cash Inflows' : 
                name === 'outflows' ? 'Cash Outflows' : 'Net Cash Flow'
              ]}
              labelStyle={{ color: "hsl(var(--foreground))" }}
              contentStyle={{
                backgroundColor: "hsl(var(--popover))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "6px"
              }}
            />
            {/* Current month reference line */}
            <ReferenceLine x={data[0]?.month} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" />
            
            {/* Cash balance area */}
            <Area
              type="monotone"
              dataKey="cashBalance"
              stroke="hsl(var(--primary))"
              strokeWidth={3}
              fill="url(#cashBalanceGradient)"
              dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}